<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WhatsappInteractiveMessageTemplatesAPI from 'dashboard/api/whatsappInteractiveMessageTemplates';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextTextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
  purpose: {
    type: String,
    default: 'send',
    validator: value => ['send', 'template'].includes(value),
  },
});

const emit = defineEmits(['send', 'save', 'close']);
const { t } = useI18n();
const dialogRef = ref(null);
const templateName = ref('');
const editingTemplateId = ref(null);
const selectedTemplateId = ref('');
const savedTemplates = ref([]);
const isLoadingTemplates = ref(false);

const defaultForm = () => ({
  type: 'button',
  body: '',
  headerType: '',
  headerText: '',
  headerMediaUrl: '',
  headerFilename: '',
  footerText: '',
  buttons: [{ title: '' }],
  ctaDisplayText: '',
  ctaUrl: '',
  flowId: '',
  flowToken: '',
  flowCta: '',
  flowAction: 'navigate',
  flowScreen: '',
  flowData: '',
  catalogThumbnailProductRetailerId: '',
});

const form = reactive(defaultForm());
const isTemplatePurpose = computed(() => props.purpose === 'template');

const dialogTitle = computed(() => {
  if (!isTemplatePurpose.value) return t('CONVERSATION.INTERACTIVE.TITLE');
  return editingTemplateId.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.EDIT_TITLE')
    : t('CONVERSATION.INTERACTIVE.TEMPLATES.NEW_TITLE');
});

const dialogDescription = computed(() =>
  isTemplatePurpose.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.FORM_DESCRIPTION')
    : t('CONVERSATION.INTERACTIVE.DESCRIPTION')
);

const confirmLabel = computed(() =>
  isTemplatePurpose.value
    ? t('CONVERSATION.INTERACTIVE.TEMPLATES.SAVE')
    : t('CONVERSATION.INTERACTIVE.SEND')
);

const savedTemplateOptions = computed(() => [
  {
    value: '',
    label: t('CONVERSATION.INTERACTIVE.TEMPLATES.EMPTY_SELECTION'),
  },
  ...savedTemplates.value.map(template => ({
    value: String(template.id),
    label: template.name,
  })),
]);

const typeOptions = computed(() => [
  { value: 'button', label: t('CONVERSATION.INTERACTIVE.TYPES.BUTTON') },
  { value: 'cta_url', label: t('CONVERSATION.INTERACTIVE.TYPES.CTA_URL') },
  {
    value: 'location_request_message',
    label: t('CONVERSATION.INTERACTIVE.TYPES.LOCATION'),
  },
  { value: 'flow', label: t('CONVERSATION.INTERACTIVE.TYPES.FLOW') },
  {
    value: 'catalog_message',
    label: t('CONVERSATION.INTERACTIVE.TYPES.CATALOG'),
  },
]);

const headerOptions = computed(() => [
  { value: '', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.NONE') },
  { value: 'text', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.TEXT') },
  { value: 'image', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.IMAGE') },
  { value: 'video', label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.VIDEO') },
  {
    value: 'document',
    label: t('CONVERSATION.INTERACTIVE.HEADER_TYPES.DOCUMENT'),
  },
]);

const flowActionOptions = computed(() => [
  { value: 'navigate', label: t('CONVERSATION.INTERACTIVE.FLOW.NAVIGATE') },
  {
    value: 'data_exchange',
    label: t('CONVERSATION.INTERACTIVE.FLOW.DATA_EXCHANGE'),
  },
]);

const flowDataError = computed(() => {
  if (!form.flowData.trim()) return '';
  try {
    const value = JSON.parse(form.flowData);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? ''
      : t('CONVERSATION.INTERACTIVE.ERRORS.FLOW_DATA');
  } catch {
    return t('CONVERSATION.INTERACTIVE.ERRORS.FLOW_DATA');
  }
});

const hasValidHeader = computed(() => {
  if (!form.headerType) return true;
  if (form.headerType === 'text') return !!form.headerText.trim();
  return /^https?:\/\//i.test(form.headerMediaUrl.trim());
});

const hasValidAction = computed(() => {
  if (form.type === 'button') {
    const buttonTitles = form.buttons.map(button => button.title.trim());
    return (
      form.buttons.length >= 1 &&
      form.buttons.length <= 3 &&
      buttonTitles.every(Boolean) &&
      new Set(buttonTitles).size === buttonTitles.length
    );
  }
  if (form.type === 'cta_url') {
    return (
      !!form.ctaDisplayText.trim() && /^https?:\/\//i.test(form.ctaUrl.trim())
    );
  }
  if (form.type === 'flow') {
    return (
      !!form.flowId.trim() &&
      !!form.flowToken.trim() &&
      !!form.flowCta.trim() &&
      !flowDataError.value
    );
  }
  return true;
});

const isValid = computed(
  () =>
    (!isTemplatePurpose.value || !!templateName.value.trim()) &&
    !!form.body.trim() &&
    form.body.length <= 1024 &&
    form.footerText.length <= 60 &&
    hasValidHeader.value &&
    hasValidAction.value
);

const actionLabel = computed(() => {
  if (form.type === 'cta_url') return form.ctaDisplayText;
  if (form.type === 'location_request_message') {
    return t('CONVERSATION.INTERACTIVE.PREVIEW.SEND_LOCATION');
  }
  if (form.type === 'flow') return form.flowCta;
  if (form.type === 'catalog_message') {
    return t('CONVERSATION.INTERACTIVE.PREVIEW.VIEW_CATALOG');
  }
  return '';
});

const reset = () => {
  Object.assign(form, defaultForm());
  templateName.value = '';
  editingTemplateId.value = null;
  selectedTemplateId.value = '';
};

const applyTemplate = template => {
  if (!template) return;

  const interactive = template.interactive || {};
  templateName.value = template.name || '';
  editingTemplateId.value = template.id || null;
  Object.assign(form, {
    ...defaultForm(),
    type: interactive.type || 'button',
    body: template.content || '',
    headerType: interactive.header_type || '',
    headerText: interactive.header_text || '',
    headerMediaUrl: interactive.header_media_url || '',
    headerFilename: interactive.header_filename || '',
    footerText: interactive.footer_text || '',
    buttons:
      interactive.type === 'button'
        ? (interactive.buttons || []).map(button => ({
            title: button.title || button.id || '',
          }))
        : defaultForm().buttons,
    ctaDisplayText: interactive.cta_display_text || '',
    ctaUrl: interactive.cta_url || '',
    flowId: interactive.flow_id || '',
    flowToken: interactive.flow_token || '',
    flowCta: interactive.flow_cta || '',
    flowAction: interactive.flow_action || 'navigate',
    flowScreen: interactive.flow_screen || '',
    flowData: interactive.flow_data
      ? JSON.stringify(interactive.flow_data, null, 2)
      : '',
    catalogThumbnailProductRetailerId:
      interactive.catalog_thumbnail_product_retailer_id || '',
  });
};

const fetchSavedTemplates = async () => {
  if (isTemplatePurpose.value) return;

  isLoadingTemplates.value = true;
  try {
    const { data } = await WhatsappInteractiveMessageTemplatesAPI.get();
    savedTemplates.value = data;
  } catch {
    savedTemplates.value = [];
  } finally {
    isLoadingTemplates.value = false;
  }
};

watch(selectedTemplateId, value => {
  if (!value) return;
  const template = savedTemplates.value.find(
    item => String(item.id) === String(value)
  );
  applyTemplate(template);
  editingTemplateId.value = null;
});

const open = template => {
  reset();
  if (template) applyTemplate(template);
  fetchSavedTemplates();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleClose = () => emit('close');

const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({ title: '' });
};

const removeButton = index => {
  if (form.buttons.length === 1) return;
  form.buttons.splice(index, 1);
};

const buildInteractiveAttributes = () => {
  const interactive = {
    type: form.type,
    header_type: form.headerType || undefined,
    header_text:
      form.headerType === 'text' ? form.headerText.trim() : undefined,
    header_media_url:
      form.headerType && form.headerType !== 'text'
        ? form.headerMediaUrl.trim()
        : undefined,
    header_filename:
      form.headerType === 'document'
        ? form.headerFilename.trim() || undefined
        : undefined,
    footer_text: form.footerText.trim() || undefined,
  };

  if (form.type === 'button') {
    interactive.buttons = form.buttons.map(button => ({
      id: button.title.trim(),
      title: button.title.trim(),
    }));
  } else if (form.type === 'cta_url') {
    interactive.cta_display_text = form.ctaDisplayText.trim();
    interactive.cta_url = form.ctaUrl.trim();
  } else if (form.type === 'flow') {
    interactive.flow_id = form.flowId.trim();
    interactive.flow_token = form.flowToken.trim();
    interactive.flow_cta = form.flowCta.trim();
    interactive.flow_action = form.flowAction;
    interactive.flow_screen = form.flowScreen.trim() || undefined;
    interactive.flow_data = form.flowData.trim()
      ? JSON.parse(form.flowData)
      : undefined;
  } else if (form.type === 'catalog_message') {
    interactive.catalog_thumbnail_product_retailer_id =
      form.catalogThumbnailProductRetailerId.trim() || undefined;
  }

  return interactive;
};

const handleConfirm = () => {
  if (!isValid.value || props.isLoading) return;
  const interactive = buildInteractiveAttributes();

  if (isTemplatePurpose.value) {
    emit('save', {
      id: editingTemplateId.value,
      name: templateName.value.trim(),
      content: form.body.trim(),
      interactive,
    });
    return;
  }

  emit('send', {
    message: form.body.trim(),
    private: false,
    contentType: 'whatsapp_interactive',
    contentAttributes: { interactive },
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="5xl"
    position="top"
    overflow-y-auto
    sticky-header-footer
    :close-on-click-outside="false"
    :title="dialogTitle"
    :description="dialogDescription"
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="!isValid"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div class="flex min-w-0 flex-col gap-5">
        <section
          v-if="isTemplatePurpose"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <NextInput
            v-model="templateName"
            :label="t('CONVERSATION.INTERACTIVE.TEMPLATES.NAME')"
            :placeholder="
              t('CONVERSATION.INTERACTIVE.TEMPLATES.NAME_PLACEHOLDER')
            "
            maxlength="100"
          />
        </section>

        <section
          v-else-if="savedTemplates.length || isLoadingTemplates"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.USE_TEMPLATE') }}
            <NextSelect
              v-model="selectedTemplateId"
              :options="savedTemplateOptions"
              :disabled="isLoadingTemplates"
              class="w-full [&>select]:w-full"
            />
          </label>
          <p class="mb-0 mt-2 text-xs text-n-slate-11">
            {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.USE_TEMPLATE_HELP') }}
          </p>
        </section>

        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="mb-4 flex items-start gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-mouse-pointer-click size-4" />
            </span>
            <div>
              <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
                {{ t('CONVERSATION.INTERACTIVE.ACTION_TITLE') }}
              </h4>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('CONVERSATION.INTERACTIVE.ACTION_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.TYPE') }}
            <NextSelect
              v-model="form.type"
              :options="typeOptions"
              class="w-full [&>select]:w-full"
            />
          </label>
        </section>

        <section
          class="flex flex-col gap-4 rounded-xl border border-n-weak p-4"
        >
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ t('CONVERSATION.INTERACTIVE.CONTENT_TITLE') }}
          </h4>
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.INTERACTIVE.HEADER') }}
            <NextSelect
              v-model="form.headerType"
              :options="headerOptions"
              class="w-full [&>select]:w-full"
            />
          </label>
          <NextInput
            v-if="form.headerType === 'text'"
            v-model="form.headerText"
            :label="t('CONVERSATION.INTERACTIVE.HEADER_TEXT')"
            maxlength="60"
          />
          <template v-else-if="form.headerType">
            <NextInput
              v-model="form.headerMediaUrl"
              type="url"
              :label="t('CONVERSATION.INTERACTIVE.MEDIA_URL')"
              placeholder="https://"
            />
            <NextInput
              v-if="form.headerType === 'document'"
              v-model="form.headerFilename"
              :label="t('CONVERSATION.INTERACTIVE.FILE_NAME')"
            />
          </template>
          <NextTextArea
            v-model="form.body"
            :label="t('CONVERSATION.INTERACTIVE.BODY')"
            :placeholder="t('CONVERSATION.INTERACTIVE.BODY_PLACEHOLDER')"
            :max-length="1024"
            show-character-count
            resize
          />
          <NextInput
            v-model="form.footerText"
            :label="t('CONVERSATION.INTERACTIVE.FOOTER')"
            :placeholder="t('CONVERSATION.INTERACTIVE.FOOTER_PLACEHOLDER')"
            maxlength="60"
          />
        </section>

        <section
          class="flex flex-col gap-4 rounded-xl border border-n-weak p-4"
        >
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ t('CONVERSATION.INTERACTIVE.CONFIGURATION_TITLE') }}
          </h4>

          <template v-if="form.type === 'button'">
            <div class="flex flex-col gap-2">
              <div
                v-for="(button, index) in form.buttons"
                :key="index"
                class="grid gap-2 rounded-lg bg-n-solid-2 p-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"
              >
                <NextInput
                  v-model="button.title"
                  :label="
                    t('CONVERSATION.INTERACTIVE.BUTTON_TITLE', {
                      number: index + 1,
                    })
                  "
                  maxlength="20"
                />
                <NextButton
                  v-if="form.buttons.length > 1"
                  type="button"
                  color="ruby"
                  variant="ghost"
                  icon="i-lucide-trash-2"
                  :aria-label="t('CONVERSATION.INTERACTIVE.REMOVE_BUTTON')"
                  @click="removeButton(index)"
                />
              </div>
              <NextButton
                v-if="form.buttons.length < 3"
                type="button"
                color="slate"
                variant="faded"
                icon="i-lucide-plus"
                :label="t('CONVERSATION.INTERACTIVE.ADD_BUTTON')"
                @click="addButton"
              />
            </div>
          </template>

          <template v-else-if="form.type === 'cta_url'">
            <NextInput
              v-model="form.ctaDisplayText"
              :label="t('CONVERSATION.INTERACTIVE.LINK.TEXT')"
              maxlength="20"
            />
            <NextInput
              v-model="form.ctaUrl"
              type="url"
              :label="t('CONVERSATION.INTERACTIVE.LINK.URL')"
              placeholder="https://"
            />
          </template>

          <div
            v-else-if="form.type === 'location_request_message'"
            class="flex items-start gap-3 rounded-lg bg-n-teal-3 p-3 text-n-teal-12"
          >
            <span class="i-lucide-map-pin size-5 shrink-0" />
            <p class="mb-0 text-sm">
              {{ t('CONVERSATION.INTERACTIVE.LOCATION_DESCRIPTION') }}
            </p>
          </div>

          <template v-else-if="form.type === 'flow'">
            <div class="grid gap-4 sm:grid-cols-2">
              <NextInput
                v-model="form.flowId"
                :label="t('CONVERSATION.INTERACTIVE.FLOW.ID')"
              />
              <NextInput
                v-model="form.flowToken"
                :label="t('CONVERSATION.INTERACTIVE.FLOW.TOKEN')"
              />
              <NextInput
                v-model="form.flowCta"
                :label="t('CONVERSATION.INTERACTIVE.FLOW.CTA')"
                maxlength="20"
              />
              <label
                class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
              >
                {{ t('CONVERSATION.INTERACTIVE.FLOW.ACTION') }}
                <NextSelect
                  v-model="form.flowAction"
                  :options="flowActionOptions"
                  class="w-full [&>select]:w-full"
                />
              </label>
              <NextInput
                v-if="form.flowAction === 'navigate'"
                v-model="form.flowScreen"
                :label="t('CONVERSATION.INTERACTIVE.FLOW.SCREEN')"
              />
            </div>
            <NextTextArea
              v-model="form.flowData"
              :label="t('CONVERSATION.INTERACTIVE.FLOW.DATA')"
              :placeholder="t('CONVERSATION.INTERACTIVE.FLOW.DATA_PLACEHOLDER')"
              :message="flowDataError"
              :message-type="flowDataError ? 'error' : 'info'"
              resize
            />
          </template>

          <template v-else-if="form.type === 'catalog_message'">
            <NextInput
              v-model="form.catalogThumbnailProductRetailerId"
              :label="t('CONVERSATION.INTERACTIVE.CATALOG.PRODUCT_ID')"
              :message="t('CONVERSATION.INTERACTIVE.CATALOG.PRODUCT_HELP')"
            />
          </template>
        </section>
      </div>

      <aside class="lg:sticky lg:top-0 lg:self-start">
        <p
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('CONVERSATION.INTERACTIVE.PREVIEW.TITLE') }}
        </p>
        <div class="rounded-2xl bg-[#efeae2] p-4 dark:bg-n-solid-2">
          <div
            class="overflow-hidden rounded-xl bg-white text-[#334155] shadow-sm"
          >
            <div
              v-if="form.headerType"
              class="flex min-h-16 items-center justify-center bg-[#f1f5f9] px-4 py-3 text-sm font-semibold"
            >
              <span v-if="form.headerType === 'text'">
                {{
                  form.headerText ||
                  t('CONVERSATION.INTERACTIVE.PREVIEW.HEADER')
                }}
              </span>
              <span v-else class="flex items-center gap-2 text-[#64748b]">
                <span class="i-lucide-paperclip size-4" />
                {{
                  headerOptions.find(option => option.value === form.headerType)
                    ?.label
                }}
              </span>
            </div>
            <div class="px-4 py-3">
              <p class="mb-0 whitespace-pre-wrap text-sm">
                {{ form.body || t('CONVERSATION.INTERACTIVE.PREVIEW.BODY') }}
              </p>
              <p
                v-if="form.footerText"
                class="mb-0 mt-2 text-xs text-[#64748b]"
              >
                {{ form.footerText }}
              </p>
            </div>
            <div
              v-if="form.type === 'button'"
              class="border-t border-[#e2e8f0]"
            >
              <div
                v-for="(button, index) in form.buttons"
                :key="`${button.title}-${index}`"
                class="flex min-h-10 items-center justify-center border-b border-[#e2e8f0] px-3 text-sm font-medium text-[#0284c7] last:border-b-0"
              >
                {{
                  button.title || t('CONVERSATION.INTERACTIVE.PREVIEW.BUTTON')
                }}
              </div>
            </div>
            <div
              v-else
              class="flex min-h-11 items-center justify-center gap-2 border-t border-[#e2e8f0] px-3 text-sm font-medium text-[#0284c7]"
            >
              <span
                :class="{
                  'i-lucide-external-link': form.type === 'cta_url',
                  'i-lucide-map-pin': form.type === 'location_request_message',
                  'i-lucide-list-checks': form.type === 'flow',
                  'i-lucide-shopping-bag': form.type === 'catalog_message',
                }"
                class="size-4"
              />
              {{ actionLabel || t('CONVERSATION.INTERACTIVE.PREVIEW.ACTION') }}
            </div>
          </div>
        </div>
        <p class="mb-0 mt-3 text-xs text-n-slate-11">
          {{ t('CONVERSATION.INTERACTIVE.WINDOW_NOTICE') }}
        </p>
      </aside>
    </div>
  </Dialog>
</template>
